// Dates, anchored to Asia/Singapore.
//
// Everything in this app happens on Singapore days: a class register, a pass
// scanned at a door, a habit logged before bed. Using the device's own timezone
// means a member travelling — or simply with their phone set wrong — logs
// against the wrong day, and a streak built over weeks breaks for no reason
// they can see. So the date is pinned here rather than inherited.
//
// Singapore has no daylight saving and has been fixed at UTC+8 since 1982, so a
// constant offset is exact rather than an approximation.

const SG_OFFSET_MINUTES = 8 * 60;
const MS_PER_MINUTE = 60000;
const MS_PER_DAY = 86400000;

const pad = (n) => String(n).padStart(2, '0');

/** The wall-clock Date in Singapore, expressed in UTC fields for easy reading. */
function toSgParts(input = new Date()) {
  const date = input instanceof Date ? input : new Date(input);
  const shifted = new Date(date.getTime() + SG_OFFSET_MINUTES * MS_PER_MINUTE);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
    weekday: shifted.getUTCDay(), // 0 = Sunday
  };
}

/** 'YYYY-MM-DD' for the Singapore day the given instant falls in. */
export function sgDateISO(input = new Date()) {
  const { year, month, day } = toSgParts(input);
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Today in Singapore, as 'YYYY-MM-DD'. */
export function sgToday() {
  return sgDateISO(new Date());
}

/** 'HH:MM' in Singapore. */
export function sgTimeHHMM(input = new Date()) {
  const { hour, minute } = toSgParts(input);
  return `${pad(hour)}:${pad(minute)}`;
}

/** Parse 'YYYY-MM-DD' into whole-number parts. Returns null on nonsense. */
export function parseISODate(iso) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!match) return null;
  const [, y, m, d] = match;
  return { year: Number(y), month: Number(m), day: Number(d) };
}

/**
 * Days between two ISO dates (b − a), counted as calendar days rather than
 * elapsed hours, so it is immune to any clock weirdness between them.
 */
export function daysBetween(aIso, bIso) {
  const a = parseISODate(aIso);
  const b = parseISODate(bIso);
  if (!a || !b) return NaN;
  const au = Date.UTC(a.year, a.month - 1, a.day);
  const bu = Date.UTC(b.year, b.month - 1, b.day);
  return Math.round((bu - au) / MS_PER_DAY);
}

/** Shift an ISO date by whole days. addDays('2026-09-17', -1) → '2026-09-16'. */
export function addDays(iso, delta) {
  const parts = parseISODate(iso);
  if (!parts) return iso;
  const shifted = new Date(Date.UTC(parts.year, parts.month - 1, parts.day) + delta * MS_PER_DAY);
  return `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`;
}

/** 0 = Sunday … 6 = Saturday, for an ISO date. */
export function weekdayOf(iso) {
  const parts = parseISODate(iso);
  if (!parts) return NaN;
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).getUTCDay();
}

/**
 * The Monday of the week containing `iso`. Weeks start on Monday because the
 * tracker's week view reads Mon–Sun, matching how people talk about "this week".
 */
export function sgWeekStart(iso = sgToday()) {
  const weekday = weekdayOf(iso);
  if (Number.isNaN(weekday)) return iso;
  // Sunday (0) is the END of the week here, so it steps back six days.
  return addDays(iso, -((weekday + 6) % 7));
}

/** The seven ISO dates of the week beginning at `weekStart`. */
export function weekDates(weekStart) {
  return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
}

/** Human label for an ISO date, e.g. "Thu, 17 Sep 2026". */
export function formatSgDate(iso, options) {
  const parts = parseISODate(iso);
  if (!parts) return '';
  return new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).toLocaleDateString('en-SG', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
    ...options,
  });
}
